import { useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

// Generate a session ID that persists during the browser session
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

interface AnalyticsEvent {
  event_type: string;
  event_name: string;
  properties?: Record<string, any>;
  page_url?: string;
}

interface UseAnalyticsReturn {
  track: (event: AnalyticsEvent) => Promise<void>;
  trackPageView: (pageName: string) => Promise<void>;
  trackFeatureUsage: (featureName: string, action: string, properties?: Record<string, any>) => Promise<void>;
  trackContentInteraction: (contentType: string, contentId: string, action: string, properties?: Record<string, any>) => Promise<void>;
  trackTimeSpent: (feature: string, timeInSeconds: number) => Promise<void>;
  startTimer: (feature: string) => () => void; // Returns a function to stop the timer
}

export const useAnalytics = (): UseAnalyticsReturn => {
  const timers = useRef<Map<string, number>>(new Map());

  const track = useCallback(async (event: AnalyticsEvent) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      // Only track for authenticated users
      if (!user) {
        console.debug('Analytics skipped: user not authenticated');
        return;
      }

      const sessionId = getSessionId();

      await supabase.from('analytics_events').insert({
        user_id: user.id,
        session_id: sessionId,
        event_type: event.event_type,
        event_name: event.event_name,
        properties: event.properties || {},
        page_url: event.page_url || window.location.href,
        user_agent: navigator.userAgent
      });
    } catch (error) {
      // Silently fail analytics to not disrupt user experience
      console.debug('Analytics tracking failed:', error);
    }
  }, []);

  const trackPageView = useCallback(async (pageName: string) => {
    await track({
      event_type: 'navigation',
      event_name: 'page_view',
      properties: { page_name: pageName },
      page_url: window.location.href
    });
  }, [track]);

  const trackFeatureUsage = useCallback(async (featureName: string, action: string, properties?: Record<string, any>) => {
    await track({
      event_type: 'feature_usage',
      event_name: `${featureName}_${action}`,
      properties: {
        feature: featureName,
        action,
        ...properties
      }
    });
  }, [track]);

  const trackContentInteraction = useCallback(async (contentType: string, contentId: string, action: string, properties?: Record<string, any>) => {
    await track({
      event_type: 'content_interaction',
      event_name: `${contentType}_${action}`,
      properties: {
        content_type: contentType,
        content_id: contentId,
        action,
        ...properties
      }
    });
  }, [track]);

  const trackTimeSpent = useCallback(async (feature: string, timeInSeconds: number) => {
    await track({
      event_type: 'engagement',
      event_name: 'time_spent',
      properties: {
        feature,
        duration_seconds: timeInSeconds,
        duration_minutes: Math.round(timeInSeconds / 60 * 100) / 100
      }
    });
  }, [track]);

  const startTimer = useCallback((feature: string): (() => void) => {
    const startTime = Date.now();
    timers.current.set(feature, startTime);

    // Return a function to stop the timer
    return () => {
      const endTime = Date.now();
      const duration = (endTime - startTime) / 1000; // Convert to seconds
      timers.current.delete(feature);
      trackTimeSpent(feature, duration);
    };
  }, [trackTimeSpent]);

  return {
    track,
    trackPageView,
    trackFeatureUsage,
    trackContentInteraction,
    trackTimeSpent,
    startTimer
  };
};

// Hook for automatic time tracking on mount/unmount
export const useTimeTracking = (feature: string, dependencies: any[] = []) => {
  const { startTimer } = useAnalytics();

  useEffect(() => {
    const stopTimer = startTimer(feature);
    return stopTimer;
  }, dependencies);
};